import { Injectable, Injector, computed, effect, inject, signal } from '@angular/core';
import { AUD_TYPES } from '@core/models/ApiFields/ig-field-values.enums';
import { IgMedia } from '@core/models/ApiFields/ig-entities';
import { Dimension } from '@features/social-analysis/models/IDimensions';
import { FiltDimension } from '@features/social-analysis/models/IFilters';
import { ITabV2 } from '@features/social-analysis/models/ITabs';
import { PageFiltersService } from '../social-shared/services/page-filters.service';
import { SocialSharedService } from '../social-shared/services/social-shared.service';
import { postTabs } from '../social-listening/config/page';
import { postChartInit } from '../social-listening/config/post-chart';
import { PostUser } from '../social-listening/models/I-user';
import { PostHashtagsHttpBuilder } from '../social-listening/services/post-hashtags.http';
import { PostUsersHttpBuilder } from '../social-listening/services/post-users.http';
import { AudienceState } from '../social-shared/store/audience.store';
import { UsersPostChart } from '../social-shared/store/chart.store';
import { CommentsState } from '../social-shared/store/comments.store';
import { IgUserDetail } from '../social-shared/store/ig-user-detail.store';
import { PageFiltersState } from '../social-shared/store/page-filters.store';
import { MetadataState } from '../social-shared/store/post-metadata.store';
import { BaseState } from '../social-shared/store/state.store';
import { IgUsersTable, TagsMentionsTable } from '../social-shared/store/table.store';
import { AudTimef, timeframeOpts } from './config/audience.config';
import { socUsersFiltDims } from './config/page-filters.config';
import { COMPONENTS_ENUM } from './config/page.config';
import { socTagsMentTableInit } from './config/tags-mentions.config';
import { socUsersTableInit } from './config/users-table.config';

@Injectable({
    providedIn: 'root',
})
export class SocialUsersService {
    readonly #sharedS = inject(SocialSharedService);
    readonly #injector = inject(Injector);

    /* STATE */

    pageFilters = new PageFiltersState(socUsersFiltDims, this.#injector);
    users = new IgUsersTable(socUsersTableInit, this.#injector);
    userDetail = new IgUserDetail(this.#injector);
    tagsMentions = new TagsMentionsTable(socTagsMentTableInit, this.#injector);

    selectedPostTab = signal<ITabV2>(postTabs[0]);
    postChart = new UsersPostChart(postChartInit, this.#injector);
    postMetadata = new MetadataState(this.#injector);
    postUsers = new BaseState<PostUser>();
    postHashtags = new BaseState<string>();
    postComments = new CommentsState(this.#injector);

    selTimeFrame = signal<Dimension>(timeframeOpts[0].values[0]);
    audienceEngaged = new AudienceState(AUD_TYPES.ENGAGED, this.#injector);
    audienceReached = new AudienceState(AUD_TYPES.REACHED, this.#injector);
    audienceFollowers = new AudienceState(AUD_TYPES.FOLLOWER, this.#injector);

    availableDates = this.#sharedS.availableDates;

    setInitDates = effect(() => this.#sharedS.setInitDates(this.pageFilters));

    resetState() {
        this.pageFilters = new PageFiltersState(socUsersFiltDims, this.#injector);
        this.users = new IgUsersTable(socUsersTableInit, this.#injector);
        this.userDetail = new IgUserDetail(this.#injector);
        this.tagsMentions = new TagsMentionsTable(socTagsMentTableInit, this.#injector);

        this.selectedPostTab = signal<ITabV2>(postTabs[0]);
        this.postChart = new UsersPostChart(postChartInit, this.#injector);
        this.postMetadata = new MetadataState(this.#injector);
        this.postUsers = new BaseState<PostUser>();
        this.postHashtags = new BaseState<string>();
        this.postComments = new CommentsState(this.#injector);

        this.selTimeFrame = signal<Dimension>(timeframeOpts[0].values[0]);
        this.audienceEngaged = new AudienceState(AUD_TYPES.ENGAGED, this.#injector);
        this.audienceReached = new AudienceState(AUD_TYPES.REACHED, this.#injector);
        this.audienceFollowers = new AudienceState(AUD_TYPES.FOLLOWER, this.#injector);
    }

    /* HTTP */

    #pageFilterHttp = inject(PageFiltersService);
    #postUsersHttp = inject(PostUsersHttpBuilder);
    #postHashtagsHttp = inject(PostHashtagsHttpBuilder);

    selPostId = computed(
        () => this.tagsMentions.selected()[0]?.[IgMedia.getPrefixed('mediaId')] as string
    );
    selPostAcc = computed(
        () => this.tagsMentions.selected()[0]?.[IgMedia.getPrefixed('username')] as string
    );

    getPageFilterOpts(state: PageFiltersState, dim: FiltDimension, query?: string | null) {
        this.#pageFilterHttp.reqFilterOpts(state, dim, query);
    }

    makeAllPageReqs() {
        this.getUsers();
        this.getTagsAndMentions();
        this.reqPostDetail();
        this.reqAllAudience();
    }

    getUsers() {
        this.users.reqData(this.pageFilters);
        this.users.reqTotal(this.pageFilters);
    }

    getUserDetail(username: string) {
        this.userDetail.reqData(this.pageFilters, username);
    }

    getTagsAndMentions() {
        this.tagsMentions.reqData(this.pageFilters);
        this.tagsMentions.reqTotal(this.pageFilters);
    }

    reqPostDetail() {
        const tab = this.selectedPostTab().value;
        if (!this.selPostId()) return;

        switch (tab) {
            case COMPONENTS_ENUM.POST_CHART:
                this.getPostChartData();
                break;
            case COMPONENTS_ENUM.METADATA:
                this.getPostMetadata();
                break;
            case COMPONENTS_ENUM.COMMENTS:
                this.getPostComments();
                break;
            case COMPONENTS_ENUM.HASHTAGS:
                this.getPostUsersAndHashtags();
                break;
        }
    }

    getPostChartData() {
        this.postChart.reqData(this.pageFilters, this.selPostId());
    }

    getPostMetadata() {
        this.postMetadata.reqMetadata(this.selPostId());
    }

    getPostComments() {
        const filters = {
            postId: this.selPostId(),
            userAcc: this.selPostAcc(),
        };
        this.postComments.reqData(filters);
    }

    getCommentReplies(parentId: string) {
        const filters = {
            postId: this.selPostId(),
            userAcc: this.selPostAcc(),
            parentId: parentId,
        };
        this.postComments.reqData(filters);
    }

    getPostUsersAndHashtags() {
        this.#postUsersHttp.reqUsers(this.postUsers, this.selPostId());
        this.#postHashtagsHttp.reqHashtags(this.postHashtags, this.selPostId());
    }

    reqAllAudience() {
        this.audienceEngaged.reqAllData(this.selTimeFrame().value as AudTimef);
        this.audienceReached.reqAllData(this.selTimeFrame().value as AudTimef);
        this.audienceFollowers.reqAllData();
    }
}
